import React, { useLayoutEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { withDynamicYAxis } from '../utils/dynamicYAxis';

const BAR_LIKE_SERIES_TYPES = new Set(['bar', 'column']);
const DESKTOP_HOVER_QUERY = '(hover: hover) and (pointer: fine)';
const DESKTOP_X_AXIS_CROSSHAIR = {
  width: 1,
  color: 'rgba(255,255,255,0.22)',
  dashStyle: 'Solid',
  zIndex: 2,
};

Highcharts.setOptions({
  plotOptions: {
    line: {
      animation: { duration: 700 },
    },
    spline: {
      animation: { duration: 700 },
    },
    area: {
      animation: { duration: 700 },
    },
    areaspline: {
      animation: { duration: 700 },
    },
  },
});

function getCanUseDesktopHover() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia(DESKTOP_HOVER_QUERY).matches;
}

function applyDesktopCrosshairToAxis(axisOptions) {
  if (axisOptions === false) {
    return axisOptions;
  }

  const axis = axisOptions || {};

  if (axis.crosshair === false) {
    return axis;
  }

  return {
    ...axis,
    crosshair: {
      ...DESKTOP_X_AXIS_CROSSHAIR,
      ...(typeof axis.crosshair === 'object' ? axis.crosshair : {}),
    },
  };
}

function withDesktopHoverCrosshair(options, canUseDesktopHover) {
  if (!canUseDesktopHover || !options || typeof options !== 'object') {
    return options;
  }

  return {
    ...options,
    xAxis: Array.isArray(options.xAxis)
      ? options.xAxis.map(applyDesktopCrosshairToAxis)
      : applyDesktopCrosshairToAxis(options.xAxis),
  };
}

function hasBarLikeSeries(options) {
  if (!options) {
    return false;
  }

  if (BAR_LIKE_SERIES_TYPES.has(options.chart?.type)) {
    return true;
  }

  if (!Array.isArray(options.series)) {
    return false;
  }

  return options.series.some((serie) => BAR_LIKE_SERIES_TYPES.has(serie?.type || options.chart?.type));
}

function withoutBarLoadAnimation(options) {
  if (!hasBarLikeSeries(options)) {
    return options;
  }

  return {
    ...options,
    chart: {
      ...(options.chart || {}),
      animation: false,
    },
    plotOptions: {
      ...(options.plotOptions || {}),
      column: {
        ...(options.plotOptions?.column || {}),
        animation: false,
      },
      bar: {
        ...(options.plotOptions?.bar || {}),
        animation: false,
      },
    },
    ...(Array.isArray(options.series)
      ? {
          series: options.series.map((serie) => (
            BAR_LIKE_SERIES_TYPES.has(serie?.type || options.chart?.type)
              ? { ...serie, animation: false }
              : serie
          )),
        }
      : {}),
  };
}

export function AnimatedHighcharts({ options, animationReady = true, dynamicYAxisRounding = 'magnitude', ...props }) {
  const [canUseDesktopHover, setCanUseDesktopHover] = useState(getCanUseDesktopHover);
  const chartOptions = useMemo(
    () => withoutBarLoadAnimation(withDesktopHoverCrosshair(withDynamicYAxis(options, dynamicYAxisRounding), canUseDesktopHover)),
    [options, dynamicYAxisRounding, canUseDesktopHover]
  );

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(DESKTOP_HOVER_QUERY);
    const handleChange = () => {
      setCanUseDesktopHover(mediaQuery.matches);
    };

    handleChange();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      {...props}
    />
  );
}
